/**
 * ADR-0013 / ADR-0016 — Inventory Valuation Owner.
 *
 * Phase 3: prefer stockSnapshots.value (+ buckets) when present.
 * Fallback: Lot-FIFO / CalculationFactory (pre-snapshot / recovery).
 */

import CalculationFactory from '../../../services/calculationFactory';
import { aggregateLotsMetrics, normalizeLotsList } from '../lots/lotsIndex';
import { dedupeLotsList } from '../../../utils/lotSystem';
import { sumStockSnapshotInventoryValue } from '../snapshots';

function catalogIdSet(products = []) {
  const catalogIds = new Set();
  for (const p of products || []) {
    if (p?.id != null && p.id !== '') catalogIds.add(String(p.id));
    if (p?.productId != null && p.productId !== '') catalogIds.add(String(p.productId));
  }
  return catalogIds;
}

function normalizeSnapshots(stockSnapshots) {
  if (!stockSnapshots) return [];
  if (Array.isArray(stockSnapshots)) return stockSnapshots;
  if (typeof stockSnapshots === 'object') return Object.values(stockSnapshots);
  return [];
}

/**
 * @param {{ products?: Array, lots?: Array|object, stockSnapshots?: Array|object }} params
 * @returns {{
 *   totalValue: number,
 *   potentialProfit: number,
 *   byCategory: object,
 *   byLocation: object,
 *   source?: string,
 *   maxLedgerSequence?: number
 * }}
 */
export function calculateInventoryValue({
  products = [],
  lots = {},
  stockSnapshots = null
} = {}) {
  const snaps = normalizeSnapshots(stockSnapshots);
  if (snaps.length > 0) {
    const fromSnaps = sumStockSnapshotInventoryValue(snaps);
    // Potential profit + breakdowns still from lots if provided (snaps = value SSOT).
    let potentialProfit = 0;
    let byCategory = {};
    let byLocation = {};
    const lotsList = normalizeLotsList(lots);
    if (lotsList.length > 0 || (products || []).length > 0) {
      try {
        const fromLots = CalculationFactory.calculateInventoryValue(
          products || [],
          lotsList.length > 0 ? lotsList : lots || {}
        );
        potentialProfit = Number(fromLots.potentialProfit) || 0;
        byCategory = fromLots.byCategory || {};
        byLocation = fromLots.byLocation || {};
      } catch {
        if (lotsList.length > 0) {
          potentialProfit =
            Number(aggregateLotsMetrics(dedupeLotsList(lotsList)).potentialProfit) || 0;
        }
      }
    }
    return {
      totalValue: fromSnaps.totalValue,
      potentialProfit,
      byCategory,
      byLocation,
      source: 'stock_snapshots',
      maxLedgerSequence: fromSnaps.maxLedgerSequence
    };
  }

  const result = CalculationFactory.calculateInventoryValue(products || [], lots || {});
  return {
    ...result,
    source: result.source || 'lots_fifo'
  };
}

/**
 * Fast KPI totals — prefer snapshots, else O(lots).
 * @param {Array|object} lots
 * @param {Array|object} [stockSnapshots]
 */
export function getInventoryLotTotals(lots = {}, stockSnapshots = null) {
  const snaps = normalizeSnapshots(stockSnapshots);
  if (snaps.length > 0) {
    const fromSnaps = sumStockSnapshotInventoryValue(snaps);
    return {
      totalValue: fromSnaps.totalValue,
      potentialProfit: 0,
      byCategory: {},
      byLocation: {},
      lastUpdated: new Date().toISOString(),
      source: 'stock_snapshots',
      maxLedgerSequence: fromSnaps.maxLedgerSequence
    };
  }
  const list = dedupeLotsList(normalizeLotsList(lots));
  const metrics = aggregateLotsMetrics(list);
  return {
    totalValue: Number(metrics.value) || 0,
    potentialProfit: Number(metrics.potentialProfit) || 0,
    byCategory: {},
    byLocation: {},
    lastUpdated: new Date().toISOString(),
    source: 'lots'
  };
}

/**
 * Inventaire KPIs: catalog-scoped value from snapshots when available.
 * @param {{ products?: Array, lots?: Array|object, stockSnapshots?: Array|object }} params
 */
export function getCatalogInventoryValuation({
  products = [],
  lots = {},
  stockSnapshots = null
} = {}) {
  const productList = Array.isArray(products) ? products : [];
  const catalogIds = catalogIdSet(productList);
  const snaps = normalizeSnapshots(stockSnapshots);

  if (snaps.length > 0) {
    const filtered = catalogIds.size
      ? snaps.filter((s) => catalogIds.has(String(s?.productId)))
      : [];
    const fromSnaps = sumStockSnapshotInventoryValue(filtered);

    // Category / location still from catalog × lots (UI grid); value SSOT = snapshots.
    const allLots = normalizeLotsList(lots);
    const catalogLots =
      catalogIds.size === 0
        ? []
        : allLots.filter((lot) => catalogIds.has(String(lot?.productId)));
    const lotValuation =
      catalogLots.length > 0
        ? CalculationFactory.calculateInventoryValue(productList, catalogLots)
        : { potentialProfit: 0, byCategory: {}, byLocation: {} };

    return {
      totalValue: fromSnaps.totalValue,
      potentialProfit: Number(lotValuation.potentialProfit) || 0,
      byCategory: lotValuation.byCategory || {},
      byLocation: lotValuation.byLocation || {},
      lastUpdated: new Date().toISOString(),
      source: 'catalog_stock_snapshots',
      maxLedgerSequence: fromSnaps.maxLedgerSequence
    };
  }

  const allLots = normalizeLotsList(lots);
  const catalogLots =
    catalogIds.size === 0
      ? []
      : allLots.filter((lot) => catalogIds.has(String(lot?.productId)));

  const valuation = CalculationFactory.calculateInventoryValue(productList, catalogLots);

  return {
    totalValue: Number(valuation.totalValue) || 0,
    potentialProfit: Number(valuation.potentialProfit) || 0,
    byCategory: valuation.byCategory || {},
    byLocation: valuation.byLocation || {},
    lastUpdated: new Date().toISOString(),
    source: 'catalog_lots'
  };
}

/**
 * @param {{ products?: Array, lots?: Array|object, stockSnapshots?: Array|object }} params
 * @returns {number}
 */
export function getInventoryTotalValue(params = {}) {
  return Number(calculateInventoryValue(params).totalValue) || 0;
}

/**
 * Einzelnes Lot — Anzeigezeile (gleiche Formel wie Stock-Projektion).
 * @param {object} lot
 * @returns {number}
 */
export function getLotLinePurchaseValue(lot) {
  const qty = Number(lot?.remainingQuantity) || 0;
  const price = Number(lot?.purchaseUnitPrice ?? lot?.purchasePrice) || 0;
  return qty * price;
}

/**
 * @param {object} lot
 * @returns {number}
 */
export function getLotLinePotentialProfit(lot) {
  const qty = Number(lot?.remainingQuantity) || 0;
  const purchase = Number(lot?.purchaseUnitPrice ?? lot?.purchasePrice) || 0;
  const selling = Number(lot?.sellingUnitPrice ?? lot?.sellingPrice) || purchase;
  return qty * (selling - purchase);
}
